import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { debug } from '../utils/debug'

// Connects to data-controller="review-modal"
// Supports two modes:
// 1. Index-based: Uses reviewsDataTarget JSON + index param (for ReviewListComponent)
// 2. Value-based: Uses data-review-modal-*-value attributes on clicked element (for homepage widget)
export default class extends Controller {
  static targets = ["modal", "reviewsData", "authorName", "stars", "productName", "comments", "location", "headline", "review", "author"]
  static values = {
    headline: String,
    review: String,
    author: String,
    location: String,
    rating: Number
  }

  connect() {
    // Only parse reviews data if the target exists (index-based mode)
    if (this.hasReviewsDataTarget) {
      this.reviews = JSON.parse(this.reviewsDataTarget.textContent)
    }
    // Lazy initialize modal - don't fail if modal target isn't ready yet
    this.modalInstance = null
  }

  // Lazy getter for modal instance
  getModalInstance() {
    if (!this.modalInstance && this.hasModalTarget) {
      this.modalInstance = new Modal(this.modalTarget)
    }
    return this.modalInstance
  }

  open(event) {
    // Prevent event bubbling (important when button is inside clickable div)
    event.stopPropagation()

    // Check if we're using index-based mode (has index param) or value-based mode
    if (event.params.index !== undefined) {
      this.openByIndex(event.params.index)
    } else {
      this.openByValues(event.currentTarget)
    }
  }

  // Index-based mode: lookup review from JSON array
  openByIndex(index) {
    const review = this.reviews[index]
    if (!review) return

    if (this.hasAuthorNameTarget) {
      this.authorNameTarget.textContent = review.authorName
    }
    if (this.hasAuthorTarget) {
      this.authorTarget.textContent = review.authorName
    }
    if (this.hasStarsTarget) {
      this.starsTarget.innerHTML = this.generateStars(review.rating)
    }
    if (this.hasProductNameTarget) {
      this.productNameTarget.textContent = review.productName || ''
      this.productNameTarget.style.display = review.productName ? 'block' : 'none'
    }
    if (this.hasCommentsTarget) {
      this.commentsTarget.textContent = review.comments || ''
    }
    if (this.hasReviewTarget) {
      this.reviewTarget.textContent = review.comments || ''
    }
    if (this.hasLocationTarget) {
      this.locationTarget.textContent = review.location || ''
    }

    this.getModalInstance()?.show()
  }

  // Value-based mode: read values from data attributes on the clicked element
  openByValues(element) {
    const headline = element.dataset.reviewModalHeadlineValue
    const review = element.dataset.reviewModalReviewValue
    const author = element.dataset.reviewModalAuthorValue
    const location = element.dataset.reviewModalLocationValue
    const rating = parseInt(element.dataset.reviewModalRatingValue, 10) || 5

    // Parse JSON strings if they were JSON-encoded
    const parsedHeadline = this.parseJsonString(headline)
    const parsedReview = this.parseJsonString(review)
    const parsedAuthor = this.parseJsonString(author)
    const parsedLocation = this.parseJsonString(location)

    // Debug logging
    debug('review-modal', 'Review modal open:', { headline: parsedHeadline, review: parsedReview?.substring(0, 50), author: parsedAuthor, location: parsedLocation, rating })

    if (this.hasHeadlineTarget) {
      this.headlineTarget.textContent = parsedHeadline || ''
    }
    if (this.hasReviewTarget) {
      this.reviewTarget.innerHTML = parsedReview || ''
    }
    if (this.hasAuthorTarget) {
      this.authorTarget.textContent = parsedAuthor || ''
    }
    if (this.hasAuthorNameTarget) {
      this.authorNameTarget.textContent = parsedAuthor || ''
    }
    if (this.hasLocationTarget) {
      this.locationTarget.textContent = parsedLocation || ''
    }
    if (this.hasStarsTarget) {
      this.starsTarget.innerHTML = this.generateStars(rating)
    }

    this.getModalInstance()?.show()
  }

  // Parse JSON string if it looks like JSON, otherwise return as-is
  parseJsonString(value) {
    if (!value) return value
    try {
      // Check if it's a JSON-encoded string (starts with quote)
      if (value.startsWith('"') || value.startsWith("'")) {
        return JSON.parse(value)
      }
      return value
    } catch {
      return value
    }
  }

  generateStars(rating) {
    const filled = Math.round(rating || 0)
    const empty = 5 - filled
    let html = ''

    for (let i = 0; i < filled; i++) {
      html += '<i class="fa-solid fa-star" style="color: #933;"></i>'
    }
    for (let i = 0; i < empty; i++) {
      html += '<i class="fa-regular fa-star" style="color: #933;"></i>'
    }

    return html
  }
}
